// Free tools, new design (/tools behind the New design flag): its own Vite entry. Starts Alpine with the tool runner
// (tools/tool-runner.js, shared with the current design) and the few x-ui parts the pages use: the top bar, selects,
// segmented controls, copy buttons and the player for sound covers. No Livewire; from app.js only the consent-gated
// PostHog (tracking-consent.js). The audio tools' heavy code (tools/audio-*.js) still loads only when a file is opened.
//
// What the new design changes in the runner (louder()):
// - the email gate is inline in the form card: email, then a 6-digit code in six boxes (a pasted code fills them all),
//   then "Verified for 30 days", remembered on this browser for the lock line;
// - the daily limit shows in the result card with the exact counts and when it resets;
// - a lookup is followed in place: the result card polls /tools/lookups/{run}/{token}/state (queue place and progress,
//   then the result) instead of opening the status page.
import './alpine/ui-tip';
import './alpine/ui-rows';
import './alpine/ui-undo';
import './alpine/ui-drop';
import './tracking-consent';
import './experiments';
import Alpine from 'alpinejs';
import { uiPlayerStore, uiSegmented, uiCopy } from './alpine/ui';
import { uiDropdown, uiDialog, uiSelect } from './alpine/ui-controls';
import { uiTopbar } from './alpine/ui-nav';
import { uiSwarm } from './alpine/ui-swarm';
import toolRunner, { bpmKeyFinder, genreFinder, followerStats, audioTool, vocalRange } from './tools/tool-runner';

const VERIFIED_KEY = 'tc_tools_verified_until';
const VERIFIED_DAYS = 30;
const POLL_MS = 3000;

const verifiedUntil = () => {
    try {
        return Number(localStorage.getItem(VERIFIED_KEY) || 0);
    } catch {
        return 0;
    }
};

/** Polls a lookup's state fragment until it is no longer pending. `l` is the reactive lookup object. */
async function poll(l, isCurrent, schedule) {
    const url = l.state + (l.from !== null ? `?from=${l.from}` : '');
    try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (response.ok && isCurrent()) {
            const html = await response.text();
            const position = html.match(/data-lookup-position="(\d+)"/);
            if (position && l.from === null) l.from = Number(position[1]);
            l.status = response.headers.get('X-Lookup-Status') || 'pending';
            l.html = html;
        }
    } catch {
        // Offline for a moment: try again on the next tick.
    }
    if (isCurrent() && l.status === 'pending') schedule(() => poll(l, isCurrent, schedule));
}

function louder(factory) {
    return (config) => {
        const base = factory(config);

        return {
            ...base,
            signedIn: !!config.signedIn,
            verified: false,
            justVerified: false,
            codePhase: 'type', // the code field: type, check (sent), ok (fusing into Verified), no (shaking, draining)
            lookup: null, // { url, state, html, status, from, cached }
            lookupTimer: null,

            init() {
                base.init.call(this);
                this.verified = this.signedIn || verifiedUntil() > Date.now();
            },

            verifiedEmail() {
                try {
                    localStorage.setItem(VERIFIED_KEY, String(Date.now() + VERIFIED_DAYS * 864e5));
                } catch {
                    // Private mode: the cookie still works, only the lock line won't remember it.
                }
                this.verified = true;
                this.justVerified = true;
            },

            openGate() {
                base.openGate.call(this);
                this.codePhase = 'type';
                this.$nextTick(() => {
                    this.$refs.gate?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    this.$refs.gateEmail?.focus();
                });
            },

            // ------------------------------------------------- 6-digit code (Bencho's one-time code)
            // One real field (x-ref code) under six drawn cells. A full code checks itself; right, the cells fuse into
            // Verified and the gate closes a beat later; wrong, the row shakes and drains, then empties for a new try.

            codeInput(event) {
                if (this.codePhase !== 'type') return;
                const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
                event.target.value = digits;
                this.code = digits;
                if (digits) this.gateErrors = {};
                if (digits.length === 6) this.confirmCode();
            },

            codePaste(event) {
                const digits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
                if (!digits || this.codePhase !== 'type') return;
                event.preventDefault();
                event.target.value = digits;
                this.codeInput(event);
            },

            async confirmCode() {
                if (this.gateBusy || this.code.length !== 6) return;
                const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
                const pause = (ms) => new Promise((r) => setTimeout(r, still ? 0 : ms));
                this.gateBusy = true;
                this.gateErrors = {};
                this.gateMessage = null;
                this.codePhase = 'check';
                let verified = false;
                try {
                    const [{ response, data }] = await Promise.all([this.post(this.urls.confirm, { email: this.email, code: this.code }), pause(420)]);
                    if (response.ok) {
                        verified = true;
                        this.codePhase = 'ok';
                        this.$refs.code?.blur();
                        await pause(900);
                    } else {
                        if (response.status === 422) this.gateErrors = data.errors || {};
                        else this.gateMessage = 'Too many attempts. Please wait a few minutes and try again.';
                        this.codePhase = 'no';
                        await pause(1000);
                        this.code = '';
                        this.codePhase = 'type';
                        this.$nextTick(() => this.$refs.code?.focus());
                    }
                } catch {
                    this.gateMessage = 'We couldn\'t check the code. Please try again.';
                    this.codePhase = 'type';
                } finally {
                    this.gateBusy = false;
                }
                if (verified) {
                    this.gateOpen = false;
                    this.codePhase = 'type';
                    this.verifiedEmail();
                    await this.run();
                }
            },

            // ------------------------------------------------- formatting

            /** Whole dollars, for the big headline range ("$400 – $1,000"). */
            dollarRange(low, high) {
                const d = (v) => '$' + Math.round(Number(v || 0)).toLocaleString('en-US');
                return Math.round(low) === Math.round(high) ? d(low) : `${d(low)} – ${d(high)}`;
            },

            /** Money calculator: where a peer percentile sits on the log scale from p25 / 4 to p75 × 4 (as in its result view). */
            peerPos(key) {
                const p = this.result?.peers;
                if (!p) return 0;
                const lo = p.p25 / 4;
                const hi = p.p75 * 4;
                return Math.round((Math.log(p[key] / lo) / Math.log(hi / lo)) * 1000) / 10;
            },

            /** Best time dot swarm: one row per weekday, thick where the slot ranks high (its level 0-4), with each hour's words. */
            swarmRows() {
                const rows = this.result?.rows;
                if (!rows) return [];
                return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => ({
                    label: d,
                    short: d,
                    v: Array.from({ length: 24 }, (_, h) => (rows[i]?.[h]?.level ?? null) === null ? null : rows[i][h].level + 1),
                    t: Array.from({ length: 24 }, (_, h) => this.cellTitle(i, h, d)),
                }));
            },


            signed(v) {
                if (v === null || v === undefined) return '';
                return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toLocaleString('en-US');
            },

            // ------------------------------------------------- daily limit

            resetsIn() {
                const at = Date.parse(this.limitInfo?.resets_at || '');
                if (!at) return '';
                const minutes = Math.max(1, Math.round((at - Date.now()) / 60000));
                const h = Math.floor(minutes / 60);
                const m = minutes % 60;
                return h ? `${h} h ${m} min` : `${m} min`;
            },

            // ------------------------------------------------- lookups in place

            async submitRun(payload) {
                this.stopLookup();
                return base.submitRun.call(this, payload);
            },

            followLookup(data) {
                this.stopLookup();
                this.isSample = false;
                this.result = null;
                this.remaining = data.remaining ?? null;
                this.lookup = { url: data.redirect, state: `${data.redirect}/state`, html: '', status: 'pending', from: null, cached: !!data.cached };
                const l = this.lookup;
                poll(l, () => this.lookup === l, (next) => (this.lookupTimer = setTimeout(next, POLL_MS)));
                this.$nextTick(() => this.$refs.result?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
            },

            stopLookup() {
                clearTimeout(this.lookupTimer);
                this.lookup = null;
            },
        };
    };
}

/**
 * Free tools A/B test (App\Tools\UpsellExperiment): asks for the visitor's arm once, on their first free result, so
 * every arm (control too) is exposed at the same moment. Anything that fails renders control: nothing added.
 */
function toolUpsellTest(flag, arms) {
    return {
        arm: null,
        closed: false,
        start() {
            if (this.arm !== null) return;
            // ?upsell=cut shows an arm for screenshots and checks, without entering the test.
            const preview = new URLSearchParams(location.search).get('upsell');
            if (arms.includes(preview)) return this.show(preview);
            if (!window.tokchartExperiments) return;
            this.arm = 'pending';
            window.tokchartExperiments.render(flag, (variant) => this.show(variant));
        },
        show(arm) {
            this.arm = arm;
            // An arm takes the place of the soft line under the result (calculators and lookups alike).
            if (arm !== 'control') document.body.dataset.upsellArm = arm;
        },
        close() {
            this.closed = true;
        },
    };
}

/** The lookup status page (emailed links): the server renders the state; this keeps polling while it is pending. */
function toolLookupPage(state, status) {
    return {
        lookup: { state, status, html: '', from: null },
        init() {
            const m = this.$root.innerHTML.match(/data-lookup-position="(\d+)"/);
            if (m) this.lookup.from = Number(m[1]);
            if (status === 'pending') {
                const l = this.lookup;
                setTimeout(() => poll(l, () => true, (next) => setTimeout(next, POLL_MS)), POLL_MS);
            }
        },
    };
}

// Signed in, the layout loads Livewire for the app shell's search (html[data-livewire]); Livewire brings and starts its
// own Alpine, so register on alpine:init instead of starting a second one. Visitors: this entry starts Alpine.
const components = {
    uiDropdown,
    uiDialog,
    uiTopbar,
    uiSelect,
    uiSegmented,
    uiCopy,
    uiSwarm,
    followerStats,
    toolLookupPage,
    toolUpsellTest,
    toolRunner: louder(toolRunner),
    bpmKeyFinder: louder(bpmKeyFinder),
    genreFinder: louder(genreFinder),
    audioTool: louder(audioTool),
    vocalRange: louder(vocalRange),
};
const register = (A) => {
    A.store('uiPlayer', uiPlayerStore);
    Object.entries(components).forEach(([name, fn]) => A.data(name, fn));
};
if (document.documentElement.hasAttribute('data-livewire')) {
    document.addEventListener('alpine:init', () => register(window.Alpine));
} else {
    window.Alpine = Alpine;
    register(Alpine);
    Alpine.start();
}
